import React, { useMemo } from 'react';

import { rem, Flex, Button } from '@vimeo/bokeh';
import {
  trackArchivePoll,
  trackOpenPoll,
  trackPublishPollResults,
} from '@vimeo/live-core/tracking/tracking_engagement_polls';
import { EPollState, IPoll, IPollItemActions } from '@vimeo/live-core/types/interaction_tools';

import { createInteractionDomName } from '../../../../libs/shared/utils/dom/identifying';
import { translations } from '../../../../translations';

interface IPollItemControlsProps {
  poll: IPoll;
  isPollShownInActiveScene?: boolean;
  isAnyPollPublished?: boolean;
  canOpenPoll: boolean;
  itemActions: IPollItemActions;
}

function getButtons({
  poll,
  canOpenPoll,
  onPollOpen,
  onPollClose,
  onToggleShowPoll,
  onPollArchive,
  isPollShownInActiveScene,
  isAnyPollPublished,
}: any) {
  return {
    open: (
      <Button
        key={'open'}
        className={createInteractionDomName('poll-item-action')}
        isDisabled={!canOpenPoll}
        size={'sm'}
        variant={'secondary'}
        onClick={(event) => {
          event.stopPropagation();
          onPollOpen(poll);

          if (isAnyPollPublished) {
            trackArchivePoll();
          }

          trackOpenPoll();
        }}
      >
        {translations.openPoll}
      </Button>
    ),
    archive: (
      <Button
        key={'archive'}
        className={createInteractionDomName('poll-item-action')}
        variant={'secondary'}
        size={'sm'}
        onClick={(event) => {
          event.stopPropagation();
          onPollArchive(poll);
          trackArchivePoll();
        }}
      >
        {translations.archivePoll}
      </Button>
    ),
    publish: (
      <Button
        key={'publish'}
        className={createInteractionDomName('poll-item-action')}
        size={'sm'}
        variant={'primary'}
        onClick={(event) => {
          event.stopPropagation();
          onPollClose(poll);
          trackPublishPollResults();
        }}
      >
        {translations.publishResults}
      </Button>
    ),
    showResults: (
      <Button
        key={'show-results'}
        className={createInteractionDomName('poll-item-action')}
        size={'sm'}
        variant={isPollShownInActiveScene ? 'destructive' : 'secondary'}
        onClick={(event) => {
          event.stopPropagation();
          onToggleShowPoll && onToggleShowPoll(poll);
        }}
      >
        {isPollShownInActiveScene ? translations.hideFromScene : translations.showOnScene}
      </Button>
    ),
  };
}

export function PollItemActions({
  poll,
  isPollShownInActiveScene,
  isAnyPollPublished,
  canOpenPoll,
  itemActions: { onPollOpen, onPollArchive, onPollClose, onToggleShowPoll },
}: IPollItemControlsProps) {
  const buttons = useMemo(
    () =>
      getButtons({
        poll,
        canOpenPoll,
        onPollOpen,
        onPollArchive,
        onPollClose,
        onToggleShowPoll,
        isPollShownInActiveScene,
        isAnyPollPublished,
      }),
    [
      poll,
      canOpenPoll,
      onPollOpen,
      onPollArchive,
      onPollClose,
      onToggleShowPoll,
      isPollShownInActiveScene,
      isAnyPollPublished,
    ],
  );

  const showResultButton = onToggleShowPoll ? buttons.showResults : null;

  const actions = useMemo(() => {
    switch (poll.state) {
      case EPollState.CLOSED:
        return [buttons.archive, showResultButton];
      case EPollState.OPENED:
        return [buttons.publish, showResultButton];
      case EPollState.DRAFT:
        return [buttons.open, showResultButton];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poll, buttons]);

  return poll.state !== EPollState.ARCHIVED ? (
    <Flex direction={'column'} gap={rem(8)}>
      {actions}
    </Flex>
  ) : null;
}
